import { useState, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  useProducts,
  useCreateOpportunityLineItem,
  useRequirePricedProducts,
} from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import { useDebounce } from '@/hooks/use-debounce'
import { formatCurrency } from '@/lib/utils'
import { Search, ArrowLeft, Trash2, Loader2 } from 'lucide-react'
import { NoPriceLabel } from '@/components/NoPriceLabel'
import type { Product } from '@/types'

interface AddProductsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunityId: string
  existingProductIds?: string[]
  /** Deal currency — shown on the panel and used to format prices. */
  currency?: string
}

interface SelectedProduct {
  product: Product
  quantity: number
  unit_price: number
  /** List price in the deal currency; null when there is none in that currency. */
  list_price: number | null
  discount: number
  description: string
}

/**
 * How far below list this line sits, as a percentage of the list total.
 * Negative means above list. Null when there is no list price to compare to —
 * which must not be shown as 0%, since that reads as "at list".
 */
function varianceFromList(item: SelectedProduct): number | null {
  if (item.list_price === null || item.list_price <= 0 || item.quantity <= 0) {
    return null
  }
  const listTotal = item.list_price * item.quantity
  const actualTotal = item.unit_price * item.quantity - (item.discount || 0)
  return ((listTotal - actualTotal) / listTotal) * 100
}

export function AddProductsModal({
  open,
  onOpenChange,
  opportunityId,
  existingProductIds = [],
  currency = 'USD',
}: AddProductsModalProps) {
  const { toast } = useToast()
  const [step, setStep] = useState<1 | 2>(1)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const createLineItem = useCreateOpportunityLineItem()

  // Governance switch, off by default. When an admin turns it on the picker
  // offers only products with a real list price in this deal's currency.
  const requirePriced = useRequirePricedProducts()
  // The escape hatch. Selling must never be blocked outright by a data gap, and
  // how often reps reach for this measures how much of the gap is left.
  const [showUnpriced, setShowUnpriced] = useState(false)
  const pricedOnly = requirePriced && !showUnpriced

  const { data: productsData, isLoading } = useProducts({
    search: debouncedSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
    // Ask the API for prices in the DEAL's currency. Without this the panel
    // said "Prices shown in CAD" while displaying whatever a USD-first ladder
    // picked — the numbers were mislabelled, not converted.
    currency,
    priced_only: pricedOnly || undefined,
  })

  const products = productsData?.data || []
  const existingSet = new Set(existingProductIds)

  // Filter out already-added products
  const availableProducts = useMemo(
    () => products.filter((p) => !existingSet.has(p.id)),
    [products, existingSet]
  )

  // Only 4 of 70 products carry a product code, so the Code column renders as a
  // wall of dashes that makes the whole picker look empty. Show it only when
  // there is something to show — it returns by itself if codes get populated.
  const anyProductHasCode = useMemo(
    () => availableProducts.some((p) => !!p.product_code),
    [availableProducts]
  )

  const toggleProduct = (product: Product) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(product.id)) {
        next.delete(product.id)
      } else {
        next.add(product.id)
      }
      return next
    })
  }

  const toggleAll = () => {
    if (selectedIds.size === availableProducts.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(availableProducts.map((p) => p.id)))
    }
  }

  const handleNext = () => {
    const selected = availableProducts
      .filter((p) => selectedIds.has(p.id))
      .map((p) => ({
        product: p,
        quantity: 1,
        // Seeded from list; the rep may overwrite it. `list_price` is retained
        // separately so the variance stays visible after they do.
        unit_price: p.unit_price || 0,
        list_price: p.unit_price ?? null,
        discount: 0,
        description: '',
      }))
    setSelectedProducts(selected)
    setStep(2)
  }

  const handleBack = () => {
    setStep(1)
  }

  const updateSelectedProduct = (index: number, field: keyof SelectedProduct, value: any) => {
    setSelectedProducts((prev) => {
      const next = [...prev]
      next[index] = { ...next[index]!, [field]: value }
      return next
    })
  }

  const removeSelectedProduct = (index: number) => {
    const removed = selectedProducts[index]
    if (removed) {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        next.delete(removed.product.id)
        return next
      })
    }
    setSelectedProducts((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSave = async () => {
    // A $0.00 line is almost never intended, and 97 of them have already
    // reached customers because nothing ever asked. Confirm rather than block —
    // free trials and bundled items are legitimate.
    const zeroPriced = selectedProducts.filter((i) => !(i.unit_price > 0))
    if (zeroPriced.length > 0) {
      const names = zeroPriced.map((i) => i.product.name).join(', ')
      const ok = window.confirm(
        `${zeroPriced.length === 1 ? 'This product has' : 'These products have'} a price of ` +
          `0.00: ${names}.\n\nThe customer will see a zero-cost line. Add anyway?`
      )
      if (!ok) return
    }

    setIsSaving(true)
    let successCount = 0
    const failed: string[] = []

    for (const item of selectedProducts) {
      try {
        await createLineItem.mutateAsync({
          opportunityId,
          data: {
            product_id: item.product.id,
            quantity: item.quantity,
            unit_price: item.unit_price,
            discount: item.discount || 0,
            description: item.description || undefined,
          },
        })
        successCount++
      } catch {
        failed.push(item.product.name || item.product.id)
      }
    }

    setIsSaving(false)

    const errorCount = failed.length
    const failedList =
      failed.slice(0, 3).join(', ') + (failed.length > 3 ? ` +${failed.length - 3} more` : '')
    if (successCount > 0 && errorCount === 0) {
      toast({
        title: 'Products Added',
        description: `${successCount} product${successCount > 1 ? 's' : ''} added`,
      })
    } else if (successCount > 0 && errorCount > 0) {
      // Partial success — use destructive variant so users notice the failures
      toast({
        title: 'Some products failed',
        description: `${successCount} added, ${errorCount} failed: ${failedList}`,
        variant: 'destructive',
      })
    } else if (errorCount > 0) {
      toast({
        title: 'Failed to add products',
        description: `${errorCount} product${errorCount > 1 ? 's' : ''} failed: ${failedList}`,
        variant: 'destructive',
      })
    }

    // Reset and close
    setStep(1)
    setSelectedIds(new Set())
    setSelectedProducts([])
    setSearch('')
    onOpenChange(false)
  }

  const handleClose = () => {
    setStep(1)
    setSelectedIds(new Set())
    setSelectedProducts([])
    setSearch('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
        aria-describedby="add-products-description"
      >
        <DialogHeader>
          <DialogTitle>{step === 1 ? 'Add Products' : 'Edit Selected Products'}</DialogTitle>
          <DialogDescription id="add-products-description">
            {step === 1
              ? 'Select products to add to this opportunity'
              : 'Set quantity, price, and discount for each product'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          /* Step 1: Product Selection */
          <>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search products"
              />
            </div>

            <div className="flex items-center justify-between text-sm">
              {selectedIds.size > 0 ? (
                <span className="font-medium text-primary">
                  {selectedIds.size} product{selectedIds.size > 1 ? 's' : ''} selected
                </span>
              ) : (
                <span />
              )}
              <span className="text-xs text-muted-foreground">Prices shown in {currency}</span>
            </div>

            {/* Only rendered when the governance switch is on — otherwise there
                is nothing to escape from and the checkbox would just confuse. */}
            {requirePriced && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-unpriced"
                  checked={showUnpriced}
                  onCheckedChange={(checked) => setShowUnpriced(checked === true)}
                />
                <label htmlFor="show-unpriced" className="text-xs text-muted-foreground">
                  Show products with no {currency} price
                </label>
              </div>
            )}

            <div className="flex-1 overflow-y-auto border rounded-md">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                <table className="w-full text-sm" role="table" aria-label="Products">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 w-10">
                        <Checkbox
                          checked={
                            availableProducts.length > 0 &&
                            selectedIds.size === availableProducts.length
                          }
                          onCheckedChange={toggleAll}
                          aria-label="Select all products"
                        />
                      </th>
                      <th className="text-left p-2">Product Name</th>
                      {anyProductHasCode && <th className="text-left p-2">Code</th>}
                      <th className="text-right p-2">List Price ({currency})</th>
                      <th className="text-left p-2 hidden lg:table-cell">Description</th>
                      <th className="text-left p-2 hidden md:table-cell">Family</th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableProducts.map((product) => (
                      <tr
                        key={product.id}
                        className={`border-t cursor-pointer hover:bg-accent/50 ${selectedIds.has(product.id) ? 'bg-primary/5' : ''}`}
                        onClick={() => toggleProduct(product)}
                      >
                        <td className="p-2" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(product.id)}
                            onCheckedChange={() => toggleProduct(product)}
                            aria-label={`Select ${product.name}`}
                          />
                        </td>
                        <td className="p-2 font-medium">{product.name}</td>
                        {anyProductHasCode && (
                          <td className="p-2 text-muted-foreground">
                            {product.product_code || '-'}
                          </td>
                        )}
                        <td className="p-2 text-right">
                          {product.unit_price ? (
                            formatCurrency(product.unit_price, product.pricing_currency || currency)
                          ) : (
                            <NoPriceLabel product={product} currency={currency} />
                          )}
                        </td>
                        <td className="p-2 text-muted-foreground text-xs max-w-[300px] truncate hidden lg:table-cell">
                          {product.description || '-'}
                        </td>
                        <td className="p-2 hidden md:table-cell">
                          {product.family ? (
                            <Badge variant="outline" className="text-xs">
                              {product.family}
                            </Badge>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))}
                    {availableProducts.length === 0 && !isLoading && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-muted-foreground">
                          {search ? 'No products match your search' : 'No products available'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleNext} disabled={selectedIds.size === 0}>
                Next ({selectedIds.size})
              </Button>
            </div>
          </>
        ) : (
          /* Step 2: Edit Selected Products */
          <>
            <div className="flex-1 overflow-y-auto border rounded-md">
              <table className="w-full text-sm" role="table" aria-label="Edit selected products">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 w-8">#</th>
                    <th className="text-left p-2">Product</th>
                    <th className="text-left p-2 w-24">Quantity *</th>
                    <th className="text-left p-2 w-32">Sales Price * ({currency})</th>
                    <th className="text-left p-2 w-28">Discount ({currency})</th>
                    <th className="text-left p-2">Description</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProducts.map((item, index) => (
                    <tr key={item.product.id} className="border-t">
                      <td className="p-2 text-muted-foreground">{index + 1}</td>
                      <td className="p-2">
                        <div className="font-medium">{item.product.name}</div>
                        {item.product.product_code && (
                          <div className="text-xs text-muted-foreground">
                            {item.product.product_code}
                          </div>
                        )}
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            updateSelectedProduct(
                              index,
                              'quantity',
                              Math.max(1, parseInt(e.target.value) || 1)
                            )
                          }
                          className="h-8 text-sm"
                          aria-label={`Quantity for ${item.product.name}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) =>
                            updateSelectedProduct(
                              index,
                              'unit_price',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="h-8 text-sm"
                          aria-label={`Price for ${item.product.name}`}
                          aria-describedby={`variance-${index}`}
                        />
                        {/* Where this line sits against the governed list price.
                            Previously invisible: a rep could quote half price
                            and nothing on screen said so. */}
                        <p
                          id={`variance-${index}`}
                          className={`text-xs mt-1 ${
                            !(item.unit_price > 0)
                              ? 'text-destructive'
                              : (varianceFromList(item) ?? 0) > 10
                                ? 'text-warning-foreground'
                                : 'text-muted-foreground'
                          }`}
                        >
                          {!(item.unit_price > 0)
                            ? 'No charge — customer sees 0.00'
                            : item.list_price === null
                              ? `No ${currency} list price`
                              : (() => {
                                  const v = varianceFromList(item)
                                  if (v === null) return 'No list price set'
                                  const suffix = ''
                                  if (Math.abs(v) < 0.005) return `At list${suffix}`
                                  return v > 0
                                    ? `${v.toFixed(1)}% below list${suffix}`
                                    : `${Math.abs(v).toFixed(1)}% above list${suffix}`
                                })()}
                        </p>
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.discount || ''}
                          onChange={(e) =>
                            updateSelectedProduct(
                              index,
                              'discount',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="h-8 text-sm"
                          aria-label={`Discount for ${item.product.name}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          value={item.description}
                          onChange={(e) =>
                            updateSelectedProduct(index, 'description', e.target.value)
                          }
                          className="h-8 text-sm"
                          placeholder="Optional..."
                          aria-label={`Description for ${item.product.name}`}
                        />
                      </td>
                      <td className="p-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => removeSelectedProduct(index)}
                          aria-label={`Remove ${item.product.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-2 border-t">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-1" aria-hidden="true" />
                Back
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving || selectedProducts.length === 0}>
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" aria-hidden="true" />
                      Saving...
                    </>
                  ) : (
                    `Save (${selectedProducts.length})`
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
